import React, { useState } from 'react';
import { clsx } from 'clsx';
import {
  ChevronRightIcon,
  ChevronDownIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { formatCurrencyCode } from '@/utils/trades/mtm-calculations';
import type { MtmRunItem } from '@/types';
import { RunLineItems } from './RunLineItems';
import { netClass, dayShortFmt, timeFmt, byLabel, timeTitle } from './history-utils';

// LR-498: the MTM "By counterparty" dated run ledger — the right-pane body when a Stock-L/B
// counterparty is selected. The MTM analogue of RepoCashPoolLedger: rows = Date · Marks · By ·
// Net, each expandable inline into its RunLineItems. Newest run expanded by default.
// Presentational — the host passes the CP's already-filtered runs (newest-first).

const ROW_GRID =
  'grid grid-cols-[16px_minmax(110px,1.2fr)_minmax(60px,0.7fr)_minmax(150px,1.5fr)_116px] items-center gap-2';

interface RunLedgerRowProps {
  run: MtmRunItem;
  expanded: boolean;
  onToggle: () => void;
}

const RunLedgerRow: React.FC<RunLedgerRowProps> = ({ run, expanded, onToggle }) => {
  const net = Number(run.net);
  const when = new Date(run.marked_at);
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className={clsx(
          ROW_GRID,
          // border-l-[3px] + pl-[9px] are CONSTANT across states (content stays at 12px, aligned
          // with the pl-3 header) — only the left-border colour + bg toggle, so expanding never
          // shifts the row. Mirrors the rail selected-row treatment (border-l-lr-accent + bg-lr-row-active).
          'h-[39px] w-full border-b border-b-gray-200 border-l-[3px] pl-[9px] pr-5 text-left hover:bg-lr-row-active focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
          expanded ? 'border-l-lr-accent bg-lr-row-active' : 'border-l-transparent'
        )}
      >
        {expanded ? (
          <ChevronDownIcon className="h-4 w-4 shrink-0 text-lr-accent" aria-hidden />
        ) : (
          <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
        )}
        {/* LR-679: UTC, spelled out — the run log used to print the viewer's local clock
            unlabelled, so the same run read at a different hour (and sometimes day) than the
            trade card's Activity. Local time stays reachable on hover. */}
        <span className="min-w-0 truncate text-sm text-gray-900" title={timeTitle(when)}>
          {dayShortFmt.format(when)}{' '}
          <span className="font-mono font-normal text-gray-400">
            {timeFmt.format(when)} UTC
          </span>
        </span>
        <span className="min-w-0 truncate text-sm text-gray-500">
          {run.mark_count} {run.mark_count === 1 ? 'mark' : 'marks'}
        </span>
        <span className="flex min-w-0 items-center gap-1 text-sm text-gray-500" title={run.marked_by ?? undefined}>
          <span className="whitespace-nowrap">{byLabel(run.marked_by, run.source)}</span>
          {run.notes && (
            <DocumentTextIcon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden title={run.notes} />
          )}
        </span>
        <span className={clsx('min-w-0 truncate text-right text-sm font-semibold', netClass(net))}>
          {formatCurrencyCode(net, run.currency)}
        </span>
      </button>
      {expanded && (
        <RunLineItems
          runId={run.id}
          currency={run.currency}
          fxRate={run.fx_rate}
          net={run.net}
          enabled={expanded}
          nested
        />
      )}
    </div>
  );
};

export interface RunLedgerProps {
  /** The selected CP's Stock-L/B runs, newest-first. */
  runs: MtmRunItem[];
}

export const RunLedger: React.FC<RunLedgerProps> = ({ runs }) => {
  // Newest run expanded by default (the component is keyed by CP in the host, so this
  // re-seeds when the operator picks a different counterparty).
  const [expanded, setExpanded] = useState<Set<number>>(
    () => new Set(runs.length ? [runs[0].id] : [])
  );
  const toggle = (id: number): void =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (runs.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center px-6 py-16 text-center">
        <div className="text-sm font-semibold text-lr-fg-2">No runs for this counterparty yet</div>
        <div className="mt-1.5 max-w-[230px] text-xs leading-relaxed text-lr-faint">
          The first Stock&nbsp;L/B mark will create a run and populate this ledger.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={clsx(ROW_GRID, 'h-[39px] border-b border-gray-200 bg-lr-surface-2 pl-3 pr-5')}>
        <span aria-hidden />
        <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Date</span>
        <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Marks</span>
        <span className="text-xs font-medium uppercase tracking-wide text-gray-500">By</span>
        {/* LR-577 (feedback #68): neutral. Unlike the per-run footer/hero this header sits over
            mixed-sign rows, so it cannot name one direction — the convention goes in a tooltip
            instead, as MetricsBar already does for its exposure columns. */}
        <span
          title="+ = due to us, − = due to CP"
          className="text-right text-xs font-medium uppercase tracking-wide text-gray-500"
        >
          Net
        </span>
      </div>
      {runs.map((run) => (
        <RunLedgerRow
          key={run.id}
          run={run}
          expanded={expanded.has(run.id)}
          onToggle={() => toggle(run.id)}
        />
      ))}
    </div>
  );
};
